import React, { FormEventHandler, Fragment, useContext, useEffect, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  CalendarDaysIcon,
  CheckIcon,
  DocumentCheckIcon,
  DocumentMinusIcon,
  EnvelopeIcon,
  MapPinIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid"
import classNames from "../../utils/classNames"
import Button from "./Button"
import { ReservationProps, SiteProps } from "../../interfaces"
import moment from "moment"
import { STATUSES } from "../../constants"
import { EditReservationContext } from "../../contexts/admin/EditReservationContext"
import {
  StatusBlocked,
  StatusDeleted,
  StatusLocked,
  StatusPaid,
  StatusUnpaid,
  StatusImported,
  StatusOnHold,
} from "./StatusBadges"
import { formatDateRange } from "../../utils/formatDateRange"
import { AdminContext } from "../../contexts/AdminContext"
import DeleteConfirmationModalContent from "../Admin/Reservations/DeleteConfirmationModalContent"
import api from "../../api2"
import { HashtagIcon } from "@heroicons/react/20/solid"
import Spinner from "./Spinner"
import IInvoice from "../../interfaces/IInvoice"
import IOrder from "../../interfaces/IOrder"
import parseDateTime from "../../utils/parseDateTime"
import { useOrder } from "../Admin/Reservations/ReservationHooks/useOrder"
import { useOrderInvoice } from "../Admin/Reservations/ReservationHooks/useOrderInvoice"
import HoverTip from "./HoverTip"
import eAdminUserRole from "~/enums/eAdminUserRole"

interface ComponentProps {
  open: boolean
  setOpen: (v: boolean) => void
  header: string
  content: JSX.Element | JSX.Element[]
  onSubmit?: FormEventHandler
  isProcessing: boolean
  width?: "max-w-xl" | "max-w-2xl"
  reservation?: ReservationProps
}

const ReservationSlideoverForm = (props: ComponentProps) => {
  const {
    camp,
    showModal,
    updateLocalReservations,
    showNotification,
    currentUserRole,
    selectedReservation,
    isParkAdminUser,
  } = useContext(AdminContext)
  const {
    onUpdateReservation,
    refUpdateReservationForm,
    isUpdatingReservation,
    isDirty,
    setIsDirty,
    showStaleMessage,
  } = useContext(EditReservationContext)
  const [isLoading, setIsLoading] = useState(false)
  const [hashId, setHashId] = useState<string>(selectedReservation?.hashed_id)
  const isProcessing = isUpdatingReservation || props.isProcessing
  const { data: order } = useOrder({ orderId: props?.reservation?.order_id, includePayments: true })
  const { data: invoice, isLoading: isLoadingInvoice } = useOrderInvoice({ orderId: props?.reservation?.order_id })
  const drawerRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (showStaleMessage) {
      drawerRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }
  }, [showStaleMessage])

  useEffect(() => {
    if (isParkAdminUser && props.reservation && order && invoice) {
      console.debug('Reservation ID:', props.reservation.id);
      console.debug('Order ID:', props.reservation?.order_id);
      console.debug('Invoice ID:', invoice?.id);
    }
  }, [order])

  const handleRemove = async () => {
    // TODO: for some reason this sometimes happens 🤔 We should remove this and apply confirm
    if (!props.reservation) return

    if (props.reservation.status !== STATUSES.BLOCKED) {
      showModal(<DeleteConfirmationModalContent reservation={props.reservation} />)
    } else {
      setIsLoading(true)
      api.Reservations.remove(props.reservation.id)
        .then(({ reservations }) => {
          updateLocalReservations(reservations)
          setIsLoading(false)
        })
        .catch((error) => {
          showNotification({
            type: "error",
            title: "Error removing reservation",
            description: error.message,
          })
          console.error(error)
          setIsLoading(false)
        })
    }
  }

  const handleClickRestore = async () => {
    if (!props.reservation) return

    setIsLoading(true)
    api.Reservations.update(props.reservation.id, {
      deleted_at: null,
      lock_version: props.reservation.lock_version,
    })
      .then(({ reservation }) => {
        updateLocalReservations([reservation])
        setIsLoading(false)
      })
      .catch((error) => {
        showNotification({
          type: "error",
          title: "Error restoring reservation",
          description: error.message,
        })
        console.error(error)
        setIsLoading(false)
      })
  }

  if (!props.open) return <></>

  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-30"
        onClose={(value) => {
          props.setOpen(value)
          setIsDirty(false)
        }}
      >
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-16"
              style={{ marginTop: "0px" }}
            >
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className={classNames("pointer-events-auto w-screen", props.width || "max-w-md")}>
                  <form
                    onInput={() => setIsDirty(true)}
                    ref={refUpdateReservationForm}
                    className="flex h-full flex-col bg-white shadow-xl"
                    onSubmit={onUpdateReservation}
                  >
                    <div ref={drawerRef} className="h-0 flex-1 overflow-y-scroll overflow-x-hidden">
                      <div
                        className={classNames(
                          "pt-6 pb-4 px-4 sm:px-6",
                          props.reservation?.deleted_at ? "bg-red-50" : "bg-gray-100",
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <Dialog.Title
                            className={classNames(
                              "text-2xl font-semibold",
                              props.reservation?.deleted_at && "line-through",
                            )}
                          >
                            {props.header}
                          </Dialog.Title>
                          <div className="flex h-8 items-center">
                            <button
                              type="button"
                              className="rounded-lg bg-white hover:bg-gray-200 p-1 focus:outline-none"
                              onClick={() => props.setOpen(false)}
                              data-testid="close-button"
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-7 w-7" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center mt-2 whitespace-nowrap flex-wrap gap-x-4 sm:gap-x-6 gap-y-2">
                          <div className="flex items-center text-gray-600 max-w-full">
                            <MapPinIcon className="w-5 h-5 text-gray-600 mr-1 xs:mr-2" />
                            <div className="font-medium truncate">{props.reservation?.site?.name}</div>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <CalendarDaysIcon className="w-5 h-5 text-gray-600 mr-1 xs:mr-2" />
                            <div className="">
                              <span className="font-medium">
                                {formatDateRange(props.reservation?.start_date, props.reservation?.end_date)}
                              </span>
                            </div>
                          </div>
                          <div className="font-medium">
                            {props.reservation?.deleted_at ? (
                              <StatusDeleted deleted_at={moment(props.reservation?.deleted_at).fromNow()} />
                            ) : props.reservation?.status === STATUSES.UNPAID ? (
                              <StatusUnpaid />
                            ) : props.reservation?.status === STATUSES.PAID ? (
                              <StatusPaid refundedAmountInCents={order?.amount_refunded_in_cents} />
                            ) : [STATUSES.IMPORTED, "blocked-import"].includes(props.reservation?.status) ? (
                              <StatusImported />
                            ) : props.reservation?.status === STATUSES.ON_HOLD ? (
                              <StatusOnHold />
                            ) : (
                              <StatusBlocked />
                            )}
                          </div>
                          {props.reservation?.is_locked && (
                            <div className="font-medium">
                              <StatusLocked />
                            </div>
                          )}
                          {hashId && props.reservation?.status !== STATUSES.IMPORTED && (
                            <div className="flex items-center text-gray-600">
                              {isLoadingInvoice ? (
                                <div className="mr-0.5">
                                  <Spinner size={5} />
                                </div>
                              ) : (
                                <HashtagIcon className="w-5 h-5 text-gray-600 mr-0.5" />
                              )}
                              <a
                                className={classNames(
                                  "font-medium",
                                  isLoadingInvoice
                                    ? "text-gray-500 pointer-events-none"
                                    : "hover:underline cursor-pointer",
                                )}
                                onClick={() => !isLoadingInvoice && window.open(`/i/${hashId}`)}
                              >
                                {hashId}
                              </a>
                            </div>
                          )}
                          {props.reservation?.kind === "recurring" &&
                            (props.reservation?.invoice?.sent_at || invoice?.sent_at) &&
                            parseDateTime(props.reservation?.invoice?.sent_at || invoice?.sent_at).diffNow()
                              .milliseconds < 0 && (
                              <div className="font-medium hidden xs:block">
                                <div className="flex items-center justify-start text-gray-600">
                                  <div className="flex items-center">
                                    <EnvelopeIcon className="w-5 h-5 flex-shrink-0 mr-1 xs:mr-2" />
                                    <span className="">Invoice sent</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          {camp.tos_requires_signature && (
                            <>
                              {moment(props.reservation?.tos_agreed_at).isValid() ? (
                                <>
                                  <HoverTip
                                    content={
                                      "Rules signed on " +
                                      moment(props.reservation?.tos_agreed_at).format(camp.date_format)
                                    }
                                    panelStyles={{ right: 0, left: "auto" }}
                                    panelClassName="hidden md:block"
                                  >
                                    <div className="flex gap-2 items-center">
                                      {moment(props.reservation?.tos_agreed_at).isValid() ? (
                                        <DocumentCheckIcon className="text-gray-600 font-medium h-5 w-5" />
                                      ) : (
                                        <DocumentMinusIcon className="text-gray-600 font-medium h-5 w-5" />
                                      )}
                                      <div className="font-medium text-gray-600">Rules</div>
                                    </div>
                                  </HoverTip>

                                  <div className="flex gap-2 items-center md:hidden">
                                    {moment(props.reservation?.tos_agreed_at).isValid() ? (
                                      <DocumentCheckIcon className="text-gray-600 font-medium h-5 w-5" />
                                    ) : (
                                      <DocumentMinusIcon className="text-gray-600 font-medium h-5 w-5" />
                                    )}
                                    <div className="font-medium text-gray-600">Rules</div>
                                  </div>
                                </>
                              ) : (
                                <div className="flex gap-2 items-center">
                                  {moment(props.reservation?.tos_agreed_at).isValid() ? (
                                    <DocumentCheckIcon className="text-gray-600 font-medium h-5 w-5" />
                                  ) : (
                                    <DocumentMinusIcon className="text-gray-600 font-medium h-5 w-5" />
                                  )}
                                  <div className="font-medium text-gray-600">Rules</div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="">
                          {props.reservation?.long_term_reservation?.is_enabled_auto_charging &&
                            camp.is_enabled_auto_charging == true && (
                              <span className="mt-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                                Autocharge enabled
                              </span>
                            )}
                        </div>
                        <div className="flex justify-between mt-4 text-sm text-gray-500">
                          <span className="mr-4 italic">
                            Created {moment(props.reservation?.created_at).format(`${camp.date_format} h:mma`)}
                          </span>
                          <span className="italic">Last edited {moment(props.reservation?.updated_at).fromNow()}</span>
                        </div>
                      </div>
                      <div className="flex flex-1 flex-col justify-between">
                        <div className="divide-y divide-gray-200 px-4 sm:px-6">{props.content}</div>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 justify-between items-center px-4 sm:px-6 py-4 gap-4">
                      {currentUserRole != eAdminUserRole.ViewOnly && (
                        <>
                          <div className="">
                            {props.reservation?.deleted_at ? (
                              <Button
                                variant="blue"
                                type="button"
                                disabled={isLoading}
                                onClick={handleClickRestore}
                                className="mr-auto w-28"
                                dataTestId="restore-button"
                              >
                                Restore
                              </Button>
                            ) : props.reservation?.status === STATUSES.ON_HOLD ? (
                              <HoverTip
                                content="This reservation is on hold. Please wait for the current payment to be processed before taking any further action."
                                white
                                panelClassName="hidden md:block"
                                panelStyles={{ right: 0, left: "auto", top: "auto", bottom: "100%" }}
                              >
                                <Button
                                  variant="red"
                                  type="button"
                                  disabled
                                  onClick={handleRemove}
                                  className="mr-auto w-28"
                                >
                                  Remove
                                </Button>
                              </HoverTip>
                            ) : (
                              <Button
                                variant="red"
                                type="button"
                                disabled={isLoading}
                                onClick={handleRemove}
                                className="mr-auto w-28"
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                          <div className="flex items-center gap-4">
                            <Button
                              type="button"
                              variant="gray"
                              className={classNames("w-28", isProcessing && "pointer-events-none opacity-60")}
                              onClick={() => props.setOpen(false)}
                              disabled={props.isProcessing || isLoading}
                              dataTestId="cancel-button"
                            >
                              Cancel
                            </Button>
                            {props.reservation?.status === STATUSES.ON_HOLD ? (
                              <HoverTip
                                white
                                content="This reservation is on hold and cannot be saved. Please wait until the payment has been processed."
                                panelStyles={{ right: 0, left: "auto", top: "auto", bottom: "100%" }}
                                panelClassName="hidden md:block"
                              >
                                <Button
                                  type="submit"
                                  variant="green"
                                  className={classNames(
                                    "w-28 mr-4",
                                    props.isProcessing && "pointer-events-none opacity-60",
                                  )}
                                  disabled={
                                    props.reservation?.status === STATUSES.ON_HOLD ||
                                    props.isProcessing ||
                                    isLoading ||
                                    showStaleMessage
                                  }
                                  dataTestId="save-button"
                                >
                                  Save
                                </Button>
                              </HoverTip>
                            ) : (
                              <Button
                                type="submit"
                                variant="green"
                                className={classNames(
                                  "w-28 mr-4",
                                  props.isProcessing && "pointer-events-none opacity-60",
                                )}
                                disabled={props.isProcessing || isLoading || showStaleMessage}
                                dataTestId="save-button"
                              >
                                Save
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default ReservationSlideoverForm
